import React, { useRef, useState } from "react";
import { Camera, FileText, Loader2, X } from "lucide-react";

// Universal file uploader — base64 with size cap + visual preview.
// Accepts images by default; pass `acceptPdf` to also allow PDF uploads
// (civic-body pet-licence certificates, KYC docs, etc.).
export default function PhotoUpload({ value, onChange, label = "Photo", maxMB = 2, testid = "photo-upload", aspect = "aspect-square", helpText, acceptPdf = false }) {
  const inputRef = useRef(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onPick = (file) => {
    if (!file) return;
    setError("");
    const isImage = file.type.startsWith("image/");
    const isPdf = file.type === "application/pdf";
    if (acceptPdf) {
      if (!isImage && !isPdf) { setError("Please pick an image or PDF file"); return; }
    } else {
      if (!isImage) { setError("Please pick an image file"); return; }
    }
    if (file.size > maxMB * 1024 * 1024) { setError(`File must be under ${maxMB} MB`); return; }
    setLoading(true);
    const reader = new FileReader();
    reader.onload = (e) => { onChange(e.target.result); setLoading(false); };
    reader.onerror = () => { setError("Could not read file"); setLoading(false); };
    reader.readAsDataURL(file);
  };

  const isPdfValue = typeof value === "string" && value.startsWith("data:application/pdf");
  const accept = acceptPdf ? "image/*,application/pdf" : "image/*";
  const helper = helpText || (acceptPdf ? `JPG / PNG / PDF · max ${maxMB}MB` : `JPG / PNG · max ${maxMB}MB`);

  return (
    <div data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider font-bold text-ink-soft mb-1">{label}</div>
      <div className={`relative ${aspect} rounded-2xl border-2 border-dashed border-line bg-surface-muted/40 overflow-hidden hover:border-brand transition-colors cursor-pointer`} onClick={() => inputRef.current?.click()}>
        {value ? (
          <>
            {isPdfValue ? (
              <div className="absolute inset-0 grid place-items-center bg-brand/5">
                <div className="text-center">
                  <FileText className="w-10 h-10 mx-auto text-brand"/>
                  <div className="text-sm font-bold mt-2">PDF ready</div>
                  <div className="text-[11px] text-ink-soft mt-0.5">Tap to change</div>
                </div>
              </div>
            ) : (
              <img src={value} alt="preview" className="w-full h-full object-cover"/>
            )}
            <button type="button" onClick={(e) => { e.stopPropagation(); onChange(null); }} className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5 hover:bg-brand">
              <X className="w-4 h-4"/>
            </button>
          </>
        ) : (
          <div className="absolute inset-0 grid place-items-center text-center text-ink-soft p-4">
            {loading ? <Loader2 className="w-7 h-7 animate-spin"/> : <>
              <div>
                <Camera className="w-7 h-7 mx-auto opacity-60"/>
                <div className="text-sm font-bold mt-2">Tap to upload</div>
                <div className="text-[11px] opacity-70 mt-0.5">{helper}</div>
              </div>
            </>}
          </div>
        )}
        <input ref={inputRef} type="file" accept={accept} onChange={(e) => onPick(e.target.files?.[0])} className="hidden" data-testid={`${testid}-input`}/>
      </div>
      {error && <p className="text-xs text-brand mt-1">{error}</p>}
    </div>
  );
}
